export class Range {
    //#region Fields

    private static readonly charCode_A = 'A'.charCodeAt(0);
    private static readonly cellAddressRegExp = new RegExp('^([A-Za-z]+)(\\d+)$');

    //#endregion

    //#region Properties

    get lastRowIndex(): number {
        return this.rowIndex + this.rowCount - 1;
    }

    get lastColumnIndex(): number {
        return this.columnIndex + this.columnCount - 1;
    }

    //#endregion

    //#region C-tors

    constructor(
        public rowIndex: number,
        public columnIndex: number,
        public rowCount: number,
        public columnCount: number,
        public sheetName?: string) { }

    static fromExcelRange(excelRange: Excel.Range, getSheetName: boolean = true): Range {
        const range = new Range(
            excelRange.rowIndex,
            excelRange.columnIndex,
            excelRange.rowCount,
            excelRange.columnCount);

        if (getSheetName) {
            range.sheetName = excelRange.worksheet.name;
        }

        return range;
    }

    static parse(localAddress: string): Range | null {
        let sheetName: string;
        let address = localAddress;

        if (!address) {
            return null;
        }

        if (address.startsWith('=') === true) { // Ex: =A1
            address = address.substring(1);
        }

        const sheetSplitterIndex = address.lastIndexOf('!');

        if (sheetSplitterIndex !== -1) {
            sheetName = address.substring(0, sheetSplitterIndex).trim();

            if (sheetName.startsWith(`'`)) {
                sheetName = sheetName.substring(1, sheetName.length - 1);
            }

            address = address.substring(sheetSplitterIndex + 1).trim().split('$').join('');
        }

        if (address.length >= 2) { // Ex: A1 (single cell)
            const ranges = address
                .split(':')
                .map(v => {
                    const result = Range.cellAddressRegExp.exec(v);

                    return result
                        ? new Range(+result[2] - 1, this.getColumnIndex(result[1]), 1, 1, sheetName)
                        : null;
                });

            if (ranges.length === 1) {
                return ranges[0];
            }

            if (ranges.length === 2 && ranges.every(r => r)) {
                return new Range(
                    Math.min(ranges[0].rowIndex, ranges[1].rowIndex),
                    Math.min(ranges[0].columnIndex, ranges[1].columnIndex),
                    Math.abs(ranges[0].rowIndex - ranges[1].rowIndex) + 1,
                    Math.abs(ranges[0].columnIndex - ranges[1].columnIndex) + 1,
                    sheetName);
            }
        }

        return null;
    }

    //#endregion

    //#region Methods

    toExcelRange(source: Excel.Workbook | Excel.Worksheet | Excel.RequestContext): Excel.Range {
        let sheet: Excel.Worksheet;

        if (source instanceof Excel.Worksheet) {
            sheet = source;
        }

        if (!sheet) {
            let book: Excel.Workbook;

            if (source instanceof Excel.RequestContext) {
                book = source.workbook;
            } else if (source instanceof Excel.Workbook) {
                book = source;
            }

            sheet = this.sheetName
                ? book.worksheets.getItem(this.sheetName)
                : book.worksheets.getActiveWorksheet();
        }

        return sheet.getRangeByIndexes(
            this.rowIndex,
            this.columnIndex,
            this.rowCount,
            this.columnCount);
    }

    intersects(range: Range | Excel.Range): boolean {
        let rowIndex: number;
        let columnIndex: number;
        let lastRowIndex: number;
        let lastColumnIndex: number;

        if (range instanceof Range) {
            rowIndex = range.rowIndex;
            columnIndex = range.columnIndex;
            lastRowIndex = range.lastRowIndex;
            lastColumnIndex = range.lastColumnIndex;
        } else if (range instanceof Excel.Range) {
            rowIndex = range.rowIndex;
            columnIndex = range.columnIndex;
            lastRowIndex = range.rowIndex + range.rowCount;
            lastColumnIndex = range.columnIndex + range.columnCount;
        }

        return (this.rowIndex >= rowIndex && this.rowIndex <= lastRowIndex
            || rowIndex >= this.rowIndex && rowIndex <= this.lastRowIndex)
            && (this.columnIndex >= columnIndex && this.columnIndex <= lastColumnIndex
                || columnIndex >= this.columnIndex && columnIndex <= this.lastColumnIndex);
    }

    toString(): string {
        const rows = `[${this.rowIndex}|${this.columnIndex}|${this.rowCount}|${this.columnCount}]`;

        return this.sheetName
            ? this.sheetName + ':' + rows
            : rows;
    }

    //#endregion

    //#region Helpers

    private static getColumnIndex(columnName: string): number {
        columnName = columnName.toUpperCase();

        let sum = 0;

        for (let i = 0; i < columnName.length; i++) {
            sum *= 26;
            sum += (columnName.charCodeAt(i) - Range.charCode_A + 1);
        }

        return sum - 1;
    }

    //#endregion
}
